import { generateXLSX, XLSXRow } from '@retail/products/utils';
import dayjs from 'dayjs';
import { useAppTFunction } from '@retail/app/i18n';
import { PriceListItem, PurchaseAgreement } from '@retail/purchase-agreements/types';
import {
  SearchParamsPriceLists,
  useMutateFetchAgreementPrices,
} from './agreementPricesComparisonApi';
import { useToast } from '@retail/hooks';

import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@shared/hooks';
import { fetchPaginatedDataInParallel } from '@shared/fetch-utils';

const csvData =
  '\uFEFFMgItem-number;Finfo-number;Agreement id;Agreement name;Agreement price; Agreement Unit;Pricetype;From date;To date;';

const columnOrder = [
  'mgItemNumber',
  'finfoNumber',
  'agreementId',
  'agreementName',
  'agreementPrice',
  'agreementUnit',
  'agreementType',
  'fromDate',
  'toDate',
];

export const useExportPriceLists = (
  params: SearchParamsPriceLists,
  selectedAgreements: PurchaseAgreement[]
) => {
  const t = useAppTFunction();
  const toast = useToast();
  const rowHeaders: XLSXRow[] = useMemo(() => [csvData.split(';')], []);

  const { isOpen: isLoading, onOpen: onLoadingStart, onClose: onLoadingEnd } = useDisclosure(false);
  const { mutateAsync: loadDataForExport } = useMutateFetchAgreementPrices();

  const getAgreementPricesAsRows = useCallback(
    (priceListItem: PriceListItem[], selectedAgreements: PurchaseAgreement[]) => {
      const markupRows: XLSXRow[] = [];
      priceListItem.forEach((priceListItem) => {
        priceListItem.agreementPrices.forEach((agreementPrice) => {
          const row: Record<string, string | number | null> = {
            mgItemNumber: priceListItem.mgItemNumber,
            finfoNumber: priceListItem.finfoNumber,
            agreementId: agreementPrice.agreementId,
            agreementName:
              selectedAgreements.find((agreement) => agreement.id === agreementPrice.agreementId)
                ?.name ?? '',
            agreementPrice: agreementPrice.price ?? '',
            agreementUnit: agreementPrice.priceUnit ?? '',
            agreementType: agreementPrice.priceType + '',
            fromDate: agreementPrice.validFrom ?? '',
            validTo: agreementPrice.validTo ?? '',
          };
          const rowValues = columnOrder.map((key) => row[key]);
          markupRows.push(rowValues);
        });
      });

      return markupRows;
    },
    []
  );

  const exportToExcel = useCallback(async () => {
    onLoadingStart();

    try {
      const pageSizeChunk = 10000;

      const priceListItems = await fetchPaginatedDataInParallel({
        fetchFn: ({ page }) =>
          loadDataForExport({
            urlVariables: { ...params, page, size: pageSizeChunk },
          }),
        maxParallellRequestsCount: 3,
      });

      const updatedData = [
        ...rowHeaders,
        ...getAgreementPricesAsRows(priceListItems, selectedAgreements),
      ];

      await generateXLSX({
        rows: updatedData,
        fileName: `${t('purchaseAgreements.agreement.columns.priceList')}-${
          priceListItems[0].mgSupplierId
        }-${dayjs().format('YYYY-MM-DD')}`,
      });

      toast.success(t('export.messages.fileDownloaded'));
    } catch (error: any) {
      console.error(error);
      toast.error(t('common.genericError'));
    }

    onLoadingEnd();
  }, [
    getAgreementPricesAsRows,
    loadDataForExport,
    onLoadingEnd,
    onLoadingStart,
    params,
    rowHeaders,
    selectedAgreements,
    t,
    toast,
  ]);

  return {
    exportToExcel,
    isLoading,
  };
};
